import React from 'react';
import { noop, flow } from 'lodash';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import * as NotificationsActions from '../../actions/notifications';
import * as SettingsActions from '../../actions/settings';
import * as SettingsSelectors from '../../selectors/settings';
import * as UsernameSelectors from '../../selectors/username';
import * as TabCommunicationActionTypes from '../../actions/tabCommunication/types';
import { SocketShape } from '../../propTypes';
import formatItem from '../../../../../../shared/formatItem';
import { getTryMeDemoNotification } from '../../../../../shared/demoNotification';
import {
    REALTIME_NOTIFICATIONS_TRY_ME_EVENT_NAME,
    REALTIME_NOTIFICATIONS_SOUND_ENABLED_CHANGE_IN_LS,
    REALTIME_NOTIFICATIONS_REALTIME_ENABLED_CHANGE_IN_LS,
    REALTIME_NOTIFICATION_LOG_MODULE,
} from '../../../../../shared/constants';
import { SafeComponent } from '../../../../../../shared/SafeComponent';

class _Listener extends React.Component {
    constructor(props) {
        super(props);
        this.socketUnsubscribe = noop;
        this.handleCrossTabCommunications = this.handleCrossTabCommunications.bind(this);
    }

    componentDidMount() {
        this.onRealtimeSettingsChange();
        this.registerToTryMeDemoEvent();
        this.registerToSettingsChanges();
        this.props.tabManager.subscribe(this.handleCrossTabCommunications);
        this.registerWindowFocus();
    }

    componentDidUpdate(prevProps) {
        // If realtimeEnabled settings change
        if (this.props.realtimeEnabled !== prevProps.realtimeEnabled) {
            this.onRealtimeSettingsChange();
        }
    }

    componentWillUnmount() {
        document.removeEventListener(REALTIME_NOTIFICATIONS_TRY_ME_EVENT_NAME, this.onTryMeDemoEvent);
        window.removeEventListener('storage', this.onStorageChanges);
        document.removeEventListener(REALTIME_NOTIFICATIONS_SOUND_ENABLED_CHANGE_IN_LS, this.onCurrentTabStorageChange);
        document.removeEventListener(
            REALTIME_NOTIFICATIONS_REALTIME_ENABLED_CHANGE_IN_LS,
            this.onCurrentTabStorageChange
        );
        this.socketUnsubscribe();
    }

    onRealtimeSettingsChange = () => {
        const { realtimeEnabled } = this.props;

        if (!realtimeEnabled) {
            this.socketUnsubscribe();
            this.socketUnsubscribe = noop;
        } else {
            this.registerSocket();
        }
    };

    registerSocket() {
        const { socket } = this.props;
        this.socketUnsubscribe = socket.subscribe(this.handleNewNotification);
    }

    handleNewNotification = (notification) => {
        const { handleNewNotification } = this.props;
        const model = formatItem(notification);
        handleNewNotification(model);
    };

    onCurrentTabStorageChange = ({ detail }) => {
        this.onStorageChanges(detail);
    };

    // This method is related to listening to settings changes
    // whether it's happening in the same tab (document event)
    // or whether it's being dispatched from other opened tab via the `storage` event.
    registerToSettingsChanges = () => {
        window.addEventListener('storage', this.onStorageChanges);
        document.addEventListener(REALTIME_NOTIFICATIONS_SOUND_ENABLED_CHANGE_IN_LS, this.onCurrentTabStorageChange);
        document.addEventListener(REALTIME_NOTIFICATIONS_REALTIME_ENABLED_CHANGE_IN_LS, this.onCurrentTabStorageChange);
    };

    onStorageChanges = ({ key, newValue }) => {
        const { setSoundEnabled, setNotificationsEnabled } = this.props;

        // We do so because this value is sent from localStorage as string.
        const normalizedValue = newValue === 'true';

        switch (key) {
            case REALTIME_NOTIFICATIONS_SOUND_ENABLED_CHANGE_IN_LS:
                setSoundEnabled(normalizedValue);
                break;
            case REALTIME_NOTIFICATIONS_REALTIME_ENABLED_CHANGE_IN_LS:
                setNotificationsEnabled(normalizedValue);
                break;
            default:
                break;
        }
    };

    registerToTryMeDemoEvent = () => {
        document.addEventListener(REALTIME_NOTIFICATIONS_TRY_ME_EVENT_NAME, this.onTryMeDemoEvent);
    };

    onTryMeDemoEvent = () => {
        const { username } = this.props;
        const demoNotification = getTryMeDemoNotification(username);
        this.handleNewNotification(demoNotification);
    };

    registerWindowFocus() {
        window.addEventListener('focus', this.props.handleFocus);
    }

    handleCrossTabCommunications(event) {
        const { type } = event;

        switch (type) {
            case TabCommunicationActionTypes.NOTIFICATION_CONSUMED: {
                const notificationId = event.payload;
                this.props.markNotificationConsumed(notificationId);
                break;
            }

            default:
                break;
        }
    }

    render() {
        return null;
    }
}

_Listener.propTypes = {
    socket: SocketShape,
    username: propTypes.string.isRequired,
    handleNewNotification: propTypes.func.isRequired,
    tabManager: propTypes.object.isRequired,
    handleFocus: propTypes.func.isRequired,
    markNotificationConsumed: propTypes.func.isRequired,
    setNotificationsEnabled: propTypes.func.isRequired,
    setSoundEnabled: propTypes.func.isRequired,
    realtimeEnabled: propTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
    username: UsernameSelectors.username(state),
    realtimeEnabled: SettingsSelectors.isRealtimeEnabled(state),
});

const mapDispatchToProps = (dispatch) => ({
    handleNewNotification: (notification) => dispatch(NotificationsActions.handleNewNotification(notification)),
    handleFocus: () => dispatch(NotificationsActions.emitNotifications()),
    setSoundEnabled: (payload) => dispatch(SettingsActions.setSoundEnabled(payload)),
    setNotificationsEnabled: (payload) => dispatch(SettingsActions.setNotificationsEnabled(payload)),
    markNotificationConsumed: (notificationId) =>
        dispatch(NotificationsActions.markNotificationConsumed(notificationId)),
});

export const Listener = flow(
    connect(mapStateToProps, mapDispatchToProps),
    SafeComponent(REALTIME_NOTIFICATION_LOG_MODULE, 'Listener')
)(_Listener);
